import { useEffect } from 'react'

import type Pusher from 'pusher-js'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'

import type { SearchResult } from '@/queries/listingSearchQuery'

import {
  applyEnterpriseAuctionUpdate,
  type EntNewBidEvent,
} from './auctionUpdateUtils'

/**
 * Hook for setting up Enterprise auction update subscriptions
 */
export function useEnterpriseAuctionUpdates({
  listings,
  pusherClientEnt,
  onListingUpdate,
}: {
  listings: SearchResult[]
  pusherClientEnt?: Pusher
  onListingUpdate: (listingId: string, updatedListing: SearchResult) => void
}) {
  const uaId = useDecodedAccessToken()?.ua_id ?? null

  useEffect(() => {
    if (!pusherClientEnt) {
      return
    }

    // Subscribe to enterprise auction channels for all enterprise listings
    const subscriptions: Array<{ channelName: string; cleanup: () => void }> =
      []

    listings.forEach((listing) => {
      if (!listing.auctionEnt) return

      const { siteAbb, listingId } = listing
      if (!listingId) return

      // Why the "a"? I don't know, but we need it
      const channelName = `${siteAbb}${listingId}a`
      const channel = pusherClientEnt.subscribe(channelName)

      /*
       * Hook for manual testing, please don't uncomment this and commit it. If
       * you do uncomment it locally, you can use the following snippet in the
       * browser dev tools to trigger a simulated event (note you'll need to
       * supply a siteAbb and id for a listing on-screen as args 0 and 1):
       *
       * window.BSTOCK_fireBid('ash-i', '218', { current_bid: 5133 })
       */

      /*
      ;(window.entChannels = window.entChannels || {})[channelName] = channel
      window.BSTOCK_fireBid = (siteAbb: string, listingId: string, rest = {}) => {
        const combinedId = `${siteAbb}${listingId}`
        window.entChannels[`${combinedId}a`].handleEvent({
          event: 'newbid',
          data:  {
            auction_id: listingId,
            site_abb: siteAbb,
            current_bid: 420,
            num_bids: 5,
            winning_user_ua_id: 'deadbeef',
            currency_symbol: '$',
            ...rest,
          }})
      }
      */

      channel.bind('newbid', (e: EntNewBidEvent) => {
        const updatedListing = applyEnterpriseAuctionUpdate(listing, e, uaId)
        onListingUpdate(listingId, updatedListing)
      })

      subscriptions.push({
        channelName,
        cleanup: () => {
          channel.unbind_all()
          channel.cancelSubscription()
        },
      })
    })

    return () => {
      subscriptions.forEach(({ cleanup }) => cleanup())
    }
  }, [listings, pusherClientEnt, uaId, onListingUpdate])
}
